import { fitWithin, SCREENSHOT_MAX_BYTES, SCREENSHOT_MAX_SIDE, type FeedbackScreenshot } from '@higgins/utils';

/** Bigger than this, a file is refused before it is even opened (an iPad photo is 2–6 MB). */
const FILE_MAX_BYTES = 25 * 1024 * 1024;
/** JPEG qualities tried in turn until the picture fits SCREENSHOT_MAX_BYTES. */
const QUALITIES = [0.85, 0.7, 0.55, 0.4];

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("The picture couldn't be prepared."))), 'image/jpeg', quality),
  );
}

async function base64Of(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

/**
 * A screenshot or photo as a JPEG no wider or taller than SCREENSHOT_MAX_SIDE and no
 * bigger than SCREENSHOT_MAX_BYTES, ready to send or to keep on the device. Throws
 * an Error with a message for the user.
 */
export async function shrinkImage(file: Blob, fileName: string): Promise<FeedbackScreenshot> {
  if (!file.type.startsWith('image/')) throw new Error('Only a picture can be attached.');
  if (file.size > FILE_MAX_BYTES) throw new Error('That picture is too large to attach (25 MB at most).');
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error("That picture couldn't be opened. Try a screenshot or a JPEG.");
  }
  const size = fitWithin(bitmap.width, bitmap.height, SCREENSHOT_MAX_SIDE);
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  canvas.getContext('2d')?.drawImage(bitmap, 0, 0, size.width, size.height);
  bitmap.close();
  for (const quality of QUALITIES) {
    const blob = await toBlob(canvas, quality);
    if (blob.size <= SCREENSHOT_MAX_BYTES) {
      return { fileName: fileName.replace(/\.[^.]*$/, '') + '.jpg', base64: await base64Of(blob) };
    }
  }
  throw new Error("That picture is too detailed to attach, even when shrunk. Try a screenshot of part of the screen.");
}
